/**
 * AuthContext - Phase 4 Authentication
 *
 * Provides authentication state and merchant access control
 * Per ADMIN_PANEL_OVERVIEW.md Section 2.1-2.3
 */

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { AccessControl } from '../lib/accessControl';

interface UserProfile {
  id: string;
  merchant_id: string | null;
  full_name: string | null;
  is_platform_admin: boolean | null;
  password_set: boolean;
}

export type RoleType = 'viewer' | 'operator' | 'manager' | 'admin' | 'equipment_check';

interface UserRole {
  id: string;
  user_id: string;
  merchant_id: string;
  location_id: string | null;
  location_ids: string[] | null;
  role: RoleType;
}

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  roles: UserRole[];
  session: Session | null;
  loading: boolean;
  isPlatformAdmin: boolean;
  /**
   * The ACTIVE merchant the user is currently operating in. For a
   * single-membership user this is always their one merchant. For a
   * multi-merchant user it is the selected merchant (see setCurrentMerchant),
   * defaulting to their home merchant (profiles.merchant_id).
   */
  merchantId: string | null;
  /**
   * Locations the current user is scoped to WITHIN the active merchant.
   * `null` or empty = all locations in the merchant (merchant-wide access);
   * a non-empty array means the user is restricted to exactly those locations.
   */
  locationIds: string[] | null;
  /**
   * The distinct merchants the user has a membership in (with display names),
   * for the merchant switcher. Has >1 entry only for multi-merchant users.
   */
  availableMerchants: { id: string; name: string }[];
  /**
   * Switch the active merchant. Only accepts a merchant the user is a member
   * of; persists the choice and clears per-merchant filter state.
   */
  setCurrentMerchant: (merchantId: string) => void;
  accessControl: AccessControl;
  /** Role check evaluated against the ACTIVE merchant only. */
  hasRole: (minRole: RoleType) => boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// localStorage key for the persisted active-merchant selection.
const SELECTED_MERCHANT_KEY = 'selectedMerchantId';

function readSelectedMerchant(): string | null {
  try {
    return localStorage.getItem(SELECTED_MERCHANT_KEY);
  } catch {
    return null; // localStorage unavailable (SSR / private mode)
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [merchantNames, setMerchantNames] = useState<Record<string, string>>({});
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingUserData, setLoadingUserData] = useState(false);
  // Explicit active-merchant selection (multi-merchant only). null = use the
  // default (home merchant). Seeded from localStorage so a refresh keeps the
  // last-used merchant.
  const [selectedMerchantOverride, setSelectedMerchantOverride] = useState<string | null>(
    () => readSelectedMerchant()
  );

  // Load user profile and roles from database
  const loadUserData = async (userId: string) => {
    console.log('[AuthContext] loadUserData START for userId:', userId);
    setLoadingUserData(true);
    try {
      // Load profile (includes merchant_id and is_platform_admin)
      console.log('[AuthContext] Fetching profile...');
      let { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      // If profile fetch failed, refresh the session token and retry once
      // (handles stale auth headers after Web Locks contention or JWT expiry)
      if (profileError) {
        console.warn('[AuthContext] Profile fetch failed, refreshing session and retrying...', profileError);
        await supabase.auth.refreshSession();
        const retry = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();
        profileData = retry.data;
        profileError = retry.error;
      }

      if (profileError) {
        console.error('[AuthContext] Error loading profile after retry:', profileError);
        return;
      }

      console.log('[AuthContext] Profile loaded:', profileData);
      setProfile(profileData as UserProfile);

      // Load roles from user_merchant_roles
      console.log('[AuthContext] Fetching roles...');
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_merchant_roles')
        .select('*')
        .eq('user_id', userId);

      if (rolesError) {
        console.error('[AuthContext] Error loading roles:', rolesError);
        return;
      }

      console.log('[AuthContext] Roles loaded:', rolesData);
      const loadedRoles = (rolesData ?? []) as UserRole[];
      setRoles(loadedRoles);

      // Load display names for the merchants this user belongs to (for the
      // merchant switcher). Typically one row; skip the query if there are none.
      const merchantIds = [...new Set(loadedRoles.map(r => r.merchant_id))];
      if (merchantIds.length > 0) {
        const { data: merchantsData, error: merchantsError } = await supabase
          .from('merchants')
          .select('id, merchant_id')
          .in('id', merchantIds);
        if (merchantsError) {
          console.warn('[AuthContext] Error loading merchant names:', merchantsError);
        } else {
          setMerchantNames(
            Object.fromEntries(
              // Always label with the unique, readable merchant_id slug (display_name
              // /business_name are often duplicated or empty across a group's merchants),
              // falling back to the uuid only if merchant_id is somehow missing.
              (merchantsData ?? []).map(m => [m.id, m.merchant_id || m.id])
            )
          );
        }
      } else {
        setMerchantNames({});
      }
    } catch (error) {
      console.error('[AuthContext] Error loading user data:', error);
    } finally {
      console.log('[AuthContext] loadUserData COMPLETE, setting loadingUserData to false');
      setLoadingUserData(false);
    }
  };

  // Initialize auth state
  useEffect(() => {
    let mounted = true;
    let initialized = false;
    let currentUserId: string | null = null;

    // Get initial session (with retry for Web Locks contention across tabs)
    const initializeAuth = async () => {
      try {
        let sessionResult;
        try {
          sessionResult = await supabase.auth.getSession();
        } catch (err: unknown) {
          if (err instanceof Error && err.name === 'AbortError') {
            console.warn('[AuthContext] getSession() hit lock contention, retrying...');
            await new Promise(r => setTimeout(r, 500));
            sessionResult = await supabase.auth.getSession();
          } else {
            throw err;
          }
        }

        const session = sessionResult.data.session;

        if (!mounted) return;

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          currentUserId = session.user.id;
          await loadUserData(session.user.id);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        if (mounted) {
          setLoading(false);
          initialized = true;
        }
      }
    };

    initializeAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[AuthContext] onAuthStateChange:', event, 'initialized:', initialized, 'mounted:', mounted);

      // Skip initial auth state change event since initializeAuth handles it
      if (!mounted || !initialized) return;

      const newUserId = session?.user?.id ?? null;

      // Only reload user data if the user actually changed
      // Note: We don't reload on SIGNED_IN because that event fires on tab visibility changes
      // and would cause unnecessary reloads that can hang when the tab is inactive
      const userChanged = currentUserId !== newUserId;

      console.log('[AuthContext] userChanged:', userChanged, 'currentUserId:', currentUserId, 'newUserId:', newUserId);

      setSession(session);
      setUser(session?.user ?? null);

      if (userChanged) {
        currentUserId = newUserId;

        if (session?.user) {
          console.log('[AuthContext] Calling loadUserData for user:', session.user.id);
          await loadUserData(session.user.id);
          console.log('[AuthContext] loadUserData completed');
        } else {
          console.log('[AuthContext] User signed out, clearing profile and roles');
          setProfile(null);
          setRoles([]);
        }
      } else {
        console.log('[AuthContext] User unchanged, skipping loadUserData');
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Derived values
  const isPlatformAdmin = profile?.is_platform_admin ?? false;

  // Distinct merchants the user is a member of (memoized for stable refs).
  const membershipMerchantIds = useMemo(
    () => [...new Set(roles.map(r => r.merchant_id))],
    [roles]
  );

  // The ACTIVE merchant. Fully derived (no flash): an explicit, still-valid
  // selection wins; otherwise the home merchant (profiles.merchant_id); otherwise
  // the first membership. For a single-membership user this always equals their
  // one merchant — identical to the previous profile.merchant_id behavior.
  const merchantId = useMemo<string | null>(() => {
    if (selectedMerchantOverride && membershipMerchantIds.includes(selectedMerchantOverride)) {
      return selectedMerchantOverride;
    }
    if (profile?.merchant_id) return profile.merchant_id;
    return membershipMerchantIds[0] ?? null;
  }, [selectedMerchantOverride, membershipMerchantIds, profile?.merchant_id]);

  // The single membership row for the active merchant (UNIQUE(user_id, merchant_id)
  // guarantees at most one).
  const activeRole = useMemo<UserRole | null>(
    () => roles.find(r => r.merchant_id === merchantId) ?? null,
    [roles, merchantId]
  );

  // Merchants for the switcher (with display names). >1 entry => multi-merchant.
  const availableMerchants = useMemo(
    () => membershipMerchantIds.map(id => ({ id, name: merchantNames[id] ?? id })),
    [membershipMerchantIds, merchantNames]
  );

  // Switch the active merchant. Only among the user's memberships; persists the
  // choice and clears per-merchant filter state so filters don't leak across.
  const setCurrentMerchant = (nextMerchantId: string) => {
    if (!membershipMerchantIds.includes(nextMerchantId)) return;
    if (nextMerchantId === merchantId) return;
    try { sessionStorage.clear(); } catch { /* unavailable in some contexts */ }
    try { localStorage.setItem(SELECTED_MERCHANT_KEY, nextMerchantId); } catch { /* unavailable */ }
    setSelectedMerchantOverride(nextMerchantId);
    // Re-scope the entire app to the newly selected merchant. Individual pages
    // fetch on mount and not all of them key their queries on merchantId, so a
    // full reload is the reliable way to refetch every merchant-scoped query and
    // drop any in-memory data from the previous merchant. The selection is
    // already persisted above, so the app comes back up on the new merchant.
    try { window.location.reload(); } catch { /* non-browser env */ }
  };

  // Locations the user is scoped to WITHIN the active merchant. Prefer the new
  // `location_ids` array; fall back to the legacy scalar `location_id`.
  // `null` = merchant-wide (all locations). Memoized for a stable ref.
  const locationIds = useMemo<string[] | null>(() => {
    if (!activeRole) return null;
    if (activeRole.location_ids && activeRole.location_ids.length > 0) {
      return activeRole.location_ids;
    }
    if (activeRole.location_id) {
      return [activeRole.location_id];
    }
    return null;
  }, [activeRole]);

  // Role hierarchy: viewer (1) → operator (2) → manager (3) → admin (4)
  const roleLevel = (role: string): number => {
    switch (role) {
      case 'admin':
        return 4;
      case 'manager':
        return 3;
      case 'operator':
        return 2;
      case 'equipment_check':
        // Read-only access for generic gates; specific write access granted
        // explicitly on equipment_checks / issues / inventory_blocking_times.
        return 1;
      case 'viewer':
        return 1;
      default:
        return 0;
    }
  };

  // Check if user has at least the specified role level IN THE ACTIVE MERCHANT.
  // (Previously this took the max across all merchants, which over-granted for
  // multi-merchant users — e.g. admin in A + viewer in B looked like admin in B.)
  const hasRole = (minRole: RoleType): boolean => {
    if (isPlatformAdmin) return true; // Platform admins have all permissions
    if (!activeRole) return false;
    return roleLevel(activeRole.role) >= roleLevel(minRole);
  };

  // Sign in with email and password
  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // User data will be loaded automatically by onAuthStateChange
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  // Sign out
  const signOut = async () => {
    await supabase.auth.signOut();
    // Clear persisted filter state and the active-merchant selection so nothing
    // leaks into the next session (same tab, different user or re-login).
    try { sessionStorage.clear(); } catch { /* unavailable in some contexts */ }
    try { localStorage.removeItem(SELECTED_MERCHANT_KEY); } catch { /* unavailable */ }
    setUser(null);
    setSession(null);
    setProfile(null);
    setRoles([]);
    setMerchantNames({});
    setSelectedMerchantOverride(null);
  };

  // Refresh profile data (e.g. after setting password)
  const refreshProfile = async () => {
    if (user) {
      await loadUserData(user.id);
    }
  };

  const accessControl: AccessControl = {
    merchantId,
    locationIds,
    isPlatformAdmin,
  };

  const value: AuthContextType = {
    user,
    profile,
    roles,
    session,
    loading: loading || loadingUserData, // Keep loading true while user data is being fetched
    isPlatformAdmin,
    merchantId,
    locationIds,
    availableMerchants,
    setCurrentMerchant,
    accessControl,
    hasRole,
    signIn,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
